'use client';

import Image from 'next/image';
import type { ComponentType } from 'react';
import { useEffect, useRef, useState } from 'react';

import { BetterAuthLogo, BrowserUseLogo, CalComLogo } from '@/components/icons';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const testimonialsEnum = {
  CALCOM: 'calcom',
  BROWSERUSE: 'browseruse',
  BETTERAUTH: 'betterauth',
} as const;

type TestimonialId = (typeof testimonialsEnum)[keyof typeof testimonialsEnum];

const testimonials: {
  id: TestimonialId;
  companyName: string;
  logo: ComponentType<{ className?: string }>;
  authorName: string;
  authorTitle: string;
  authorImage: {
    src: string;
    width: number;
    height: number;
  };
  quote: React.ReactNode;
}[] = [
  {
    id: testimonialsEnum.CALCOM,
    companyName: 'Cal.com',
    logo: () => <CalComLogo className="w-14" />,
    authorName: 'Peer Richelson',
    authorTitle: 'Co-founder & CTO, Cal.com',
    authorImage: {
      src: '/peer-richelson.png',
      width: 52,
      height: 52,
    },
    quote: (
      <p>
        "<span>cubic immediately improved our review process.</span> PRs move faster and quality is
        up."
      </p>
    ),
  },
  {
    id: testimonialsEnum.BROWSERUSE,
    companyName: 'Browser Use',
    logo: () => <BrowserUseLogo className="w-24" />,
    authorName: 'Nick Sweeting',
    authorTitle: 'Founding engineer, Browser Use (YC W25)',
    authorImage: {
      src: '/nick-sweeting.png',
      width: 52,
      height: 52,
    },
    quote: (
      <p>
        "I've been a dev for 13+ years and <span>I'm routinely humbled by what cubic catches.</span>
        "
      </p>
    ),
  },
  {
    id: testimonialsEnum.BETTERAUTH,
    companyName: 'Better Auth',
    logo: () => <BetterAuthLogo className="w-24" />,
    authorName: 'Bereket Engida',
    authorTitle: 'Founder, Better Auth',
    authorImage: {
      src: '/bereket-engida.png',
      width: 52,
      height: 52,
    },
    quote: (
      <p>
        "<span>cubic helps us merge PRs so much faster.</span> It's been so good. "
      </p>
    ),
  },
];

export function TestimonialTabs() {
  const [activeTab, setActiveTab] = useState<TestimonialId>(testimonialsEnum.CALCOM);
  const [isHovered, setIsHovered] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const getNextTab = (currentTab: TestimonialId): TestimonialId => {
    const currentIndex = testimonials.findIndex((t) => t.id === currentTab);
    const nextIndex = (currentIndex + 1) % testimonials.length;
    return testimonials[nextIndex]?.id ?? testimonialsEnum.CALCOM;
  };

  const startAutoSwitch = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      setActiveTab((currentTab) => getNextTab(currentTab));
    }, 1000 * 10); // 10 seconds
  };

  const stopAutoSwitch = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    if (!isHovered) {
      startAutoSwitch();
    } else {
      stopAutoSwitch();
    }

    return () => {
      stopAutoSwitch();
    };
  }, [isHovered]);

  const handleTabChange = (value: string) => {
    setActiveTab(value as TestimonialId);
    if (!isHovered) {
      startAutoSwitch(); // Restart the timer
    }
  };

  return (
    <div
      className="flex flex-col items-center justify-center h-full max-w-[460px] w-full"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Tabs
        value={activeTab}
        className="w-full flex flex-col gap-10"
        onValueChange={handleTabChange}
      >
        <div>
          {testimonials.map((testimonial) => (
            <TabsContent key={testimonial.id} value={testimonial.id}>
              <TestimonialCard testimonial={testimonial} />
            </TabsContent>
          ))}
        </div>

        <TabsList className="w-full grid grid-cols-3 gap-4 items-center justify-center bg-transparent">
          {testimonials.map((testimonial) => (
            <TestimonialTabTrigger key={testimonial.id} value={testimonial.id}>
              <testimonial.logo />
            </TestimonialTabTrigger>
          ))}
        </TabsList>
      </Tabs>
    </div>
  );
}

function TestimonialCard({ testimonial }: { testimonial: (typeof testimonials)[number] }) {
  return (
    <div className="bg-black/70 border border-white/10 rounded-xl shadow-lg flex flex-col gap-6 h-[480px] p-16 text-base">
      <div className="text-lg font-normal flex-1 flex flex-col justify-center text-muted-foreground [&_span]:text-white [&_p]:text-xl">
        {testimonial.quote}
      </div>
      <div className="flex items-center gap-3 mt-4">
        <Image
          src={testimonial.authorImage.src}
          alt={testimonial.authorName}
          width={testimonial.authorImage.width}
          height={testimonial.authorImage.height}
          className="rounded-md border border-white/20"
        />
        <div className="flex flex-col">
          <span className="text-white font-medium text-base">{testimonial.authorName}</span>
          <span className="text-muted-foreground text-base">{testimonial.authorTitle}</span>
        </div>
      </div>
    </div>
  );
}

function TestimonialTabTrigger({ value, children }: { value: string; children: React.ReactNode }) {
  return (
    <TabsTrigger
      value={value}
      className="relative flex-1 px-0 py-2 text-base text-muted-foreground font-medium rounded-none data-[state=active]:text-white after:content-[''] after:absolute after:-bottom-2 after:left-0 after:w-full after:h-0.5 after:bg-muted-foreground/20 after:rounded-full data-[state=active]:after:bg-white/80 data-[state=active]:bg-transparent h-full"
    >
      {children}
    </TabsTrigger>
  );
}
